/** biome-ignore-all lint/suspicious/noConsole: This file is used for debugging locale settings */

import Cookies from 'cookies-js';

const localeCookieName = 'preferredLocale';

export type SupportedLocale = 'sv-SE' | 'nb-NO' | 'fi-FI';

/**
 * Get the preferred locale from cookie if set
 */
export const getPreferredLocale = (): SupportedLocale | null => {
  if (typeof window === 'undefined') return null;
  const locale = Cookies.get(localeCookieName);
  return locale as SupportedLocale | null;
};

/**
 * Set the preferred locale in a cookie
 */
export const setPreferredLocale = (locale: SupportedLocale) => {
  if (typeof window === 'undefined') return;
  Cookies.set(localeCookieName, locale, {
    expires: Number.POSITIVE_INFINITY,
  });
};

/**
 * Clear the preferred locale cookie (use market default)
 */
export const clearPreferredLocale = () => {
  if (typeof window === 'undefined') return;
  Cookies.expire(localeCookieName);
};

/**
 * Get the date picker locale code from full locale
 * sv-SE -> sv, nb-NO -> nb, fi-FI -> fi
 */
export const getDatePickerLocale = (locale: SupportedLocale): string => {
  return locale.split('-')[0];
};

const SUPPORTED_LOCALES: SupportedLocale[] = ['sv-SE', 'nb-NO', 'fi-FI'];

/**
 * Check if a locale string is valid
 */
export const isValidLocale = (locale: string): locale is SupportedLocale => {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale);
};

/**
 * Set the locale and reload the page to apply changes
 * Available globally as window.setLocale()
 */
export const setLocaleAndReload = (locale: string) => {
  // Validate locale before setting
  if (!isValidLocale(locale)) {
    console.error(
      `❌ Invalid locale: "${locale}". Supported locales are: ${SUPPORTED_LOCALES.join(', ')}`,
    );
    console.log('💡 Use window.getLocaleInfo() to see available locales');
    return;
  }

  setPreferredLocale(locale);
  console.log(`🌍 Locale changed to ${locale}. Reloading page...`);
  window.location.reload();
};

/**
 * Clear the locale cookie and reload to use market default
 * Available globally as window.clearLocale()
 */
export const clearLocaleAndReload = () => {
  const currentLocale = getPreferredLocale();
  clearPreferredLocale();
  console.log(`🌍 Locale cleared (was: ${currentLocale}). Reloading to use market default...`);
  window.location.reload();
};

/**
 * Get information about the current locale setup
 * Available globally as window.getLocaleInfo()
 */
export const getLocaleInfo = () => {
  const preferredLocale = getPreferredLocale();
  return {
    preferredLocale,
    isUsingCookie: !!preferredLocale,
    supportedLocales: SUPPORTED_LOCALES,
  };
};
